'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { config } from '@/src/config';
import TurnstileWidget, {
  TurnstileAppearance,
  TurnstileWidgetHandle,
} from '@/src/components/common/TurnstileWidget';

interface UseTurnstileOptions {
  action?: string;
  theme?: 'auto' | 'light' | 'dark';
  className?: string;
  appearance?: TurnstileAppearance;
}

interface UseTurnstileResult {
  // Current token. null when the widget hasn't produced one (or expired/error).
  token: string | null;
  // True when Turnstile is configured for this build (env present). When false,
  // forms should skip the token requirement and submit as before.
  isRequired: boolean;
  // Force the widget to issue a fresh token. Call after a server-side rejection
  // or before retrying a submit (tokens are single-use).
  reset: () => void;
  // JSX to render in the form. Returns null when isRequired is false.
  widget: React.ReactNode;
}

export function useTurnstile(opts: UseTurnstileOptions = {}): UseTurnstileResult {
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<TurnstileWidgetHandle | null>(null);

  // E2E money-path runs set localStorage `e2e_bypass=1`
  const [bypass, setBypass] = useState(false);
  useEffect(() => {
    setBypass(
      typeof window !== 'undefined' &&
        window.localStorage.getItem('e2e_bypass') === '1',
    );
  }, []);

  const isRequired = Boolean(config.TURNSTILE_SITE_KEY) && !bypass;

  const reset = useCallback(() => {
    setToken(null);
    widgetRef.current?.reset();
  }, []);

  const widget = useMemo(() => {
    if (!isRequired) return null;
    return (
      <TurnstileWidget
        ref={widgetRef}
        onToken={setToken}
        action={opts.action}
        theme={opts.theme}
        className={opts.className}
        appearance={opts.appearance}
      />
    );
  }, [isRequired, opts.action, opts.theme, opts.className, opts.appearance]);

  return { token, isRequired, reset, widget };
}
